<template>
  <div
    aria-label="Gestion du personnel logistique"
    role="main"
  >
    <h1
      id="page-title"
      class="sr-only"
    >
      Gestion des membres du personnel logistique
    </h1>
    <p
      id="page-desc"
      class="sr-only"
    >
      Cette page permet de visualiser, filtrer et gérer les membres du personnel logistique. Utilisez les filtres pour affiner la liste. Les actions sont accessibles via les boutons dédiés.
    </p>
    <!-- Statistiques synthétiques -->
    <b-row
      class="mb-3"
      aria-label="Statistiques synthétiques du personnel"
    >
      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6
                class="text-muted"
                aria-label="Nombre total de membres du personnel"
              >
                Total membres du personnel
              </h6>
              <h3 class="font-weight-bolder mb-0">
                {{ summaryStats.total || 0 }}
              </h3>
            </div>
            <div class="avatar staff-total-icon p-2">
              <feather-icon
                icon="UsersIcon"
                class="text-danger"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6
                class="text-muted"
                aria-label="Nombre de membres actifs"
              >
                Membres actifs
              </h6>
              <h3 class="font-weight-bolder mb-0 text-success">
                {{ summaryStats.active || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-success p-2">
              <feather-icon
                icon="CheckCircleIcon"
                class="text-success"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6
                class="text-muted"
                aria-label="Nombre de membres inactifs"
              >
                Membres inactifs
              </h6>
              <h3 class="font-weight-bolder mb-0 text-secondary">
                {{ summaryStats.inactive || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-secondary p-2">
              <feather-icon
                icon="XCircleIcon"
                class="text-secondary"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Statistiques par rôle logistique -->
    <b-row
      class="mb-3"
      aria-label="Statistiques par rôle logistique"
    >
      <b-col
        v-for="role in staffRoles"
        :key="role.value"
        lg="3"
        md="6"
        sm="12"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h6
                class="text-muted"
                :aria-label="'Nombre de ' + role.label.toLowerCase()"
              >
                {{ role.label }}
              </h6>
              <h3 class="font-weight-bolder mb-0 text-warning">
                {{ summaryStats[role.value] || 0 }}
              </h3>
            </div>
            <div class="avatar bg-light-warning p-2">
              <feather-icon
                icon="UserCheckIcon"
                class="text-warning"
                size="18"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Barre de recherche et filtres -->
    <b-card
      class="mb-3"
      aria-label="Filtres et actions du personnel logistique"
    >
      <b-row>
        <!-- Search Field -->
        <b-col
          md="4"
          sm="6"
        >
          <b-form-group label-class="form-label-premium">
            <label for="search-input">Recherche</label>
            <b-input-group>
              <b-form-input
                id="search-input"
                v-model="searchTerm"
                class="premium-input"
                placeholder="Rechercher par nom, email..."
                aria-label="Recherche par nom ou email"
                @input="onSearchChange"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  @click="getStaff"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <!-- Repository Filter -->
        <b-col
          md="4"
          sm="6"
        >
          <b-form-group label-class="form-label-premium">
            <label for="repo-filter">Dépôt</label>
            <v-select
              id="repo-filter"
              v-model="filters.repository"
              :options="repositories"
              label="name"
              placeholder="Tous les dépôts"
              :reduce="repo => repo.id"
              clearable
              class="enhanced-select border-select"
              aria-label="Filtrer par dépôt logistique"
              @input="getStaff"
            />
            <small
              id="repo-filter-desc"
              class="sr-only"
            >Le dépôt est requis uniquement pour les opérateurs de dépôt.</small>
          </b-form-group>
        </b-col>

        <!-- Status Filter -->
        <b-col
          md="4"
          sm="6"
        >
          <b-form-group label-class="form-label-premium">
            <label for="status-filter">Statut</label>
            <v-select
              id="status-filter"
              v-model="filters.is_active"
              :options="[
                {label: 'Tous', value: null},
                {label: 'Actif', value: true},
                {label: 'Inactif', value: false}
              ]"
              label="label"
              :reduce="item => item.value"
              placeholder="Tous"
              clearable
              class="enhanced-select border-select"
              aria-label="Filtrer par statut du personnel"
              @input="getStaff"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Action Buttons -->
      <div
        class="d-flex justify-content-between mt-3"
        aria-label="Actions principales"
      >
        <div>
          <b-button
            v-b-tooltip.hover
            title="Réinitialiser les filtres"
            variant="outline-secondary"
            class="mr-1"
            @click="resetFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
            />
          </b-button>
        </div>

        <div>
          <b-button
            v-b-tooltip.hover
            title="Vue Carte/Liste"
            :variant="viewMode === 'table' ? 'outline-secondary' : 'outline-primary'"
            class="mr-1"
            @click="toggleViewMode"
          >
            <feather-icon
              :icon="viewMode === 'table' ? 'GridIcon' : 'ListIcon'"
              size="16"
            />
          </b-button>

          <b-button
            v-b-tooltip.hover
            title="Exporter en CSV"
            variant="outline-success"
            class="mr-1"
            @click="exportToCSV"
          >
            <feather-icon
              icon="DownloadIcon"
              size="16"
            />
          </b-button>

          <b-button
            :to="{ name: 'staff-add' }"
            variant="primary"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter un membre</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- État de chargement -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <b-spinner
        variant="primary"
        aria-hidden="true"
      />
      <span class="sr-only">Chargement des membres du personnel...</span>
    </div>

    <!-- Vue carte -->
    <div
      v-else-if="viewMode === 'card' && !isLoading"
      aria-label="Vue carte des membres du personnel"
    >
      <b-row>
        <b-col
          v-for="staff in staffList"
          :key="staff.id"
          lg="4"
          md="6"
          sm="12"
          class="mb-3"
        >
          <b-card
            no-body
            class="supplier-card h-100"
            :class="{ 'border-success': staff.is_active, 'border-secondary': !staff.is_active }"
          >
            <b-card-body>
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h5
                  class="mb-0"
                  :class="staff.is_active ? 'text-dark' : 'text-muted'"
                >
                  {{ staff.username || 'Sans nom' }}
                </h5>
                <b-badge
                  pill
                  :variant="staff.is_active ? 'success' : 'secondary'"
                  class="px-3 py-2"
                >
                  <feather-icon
                    :icon="staff.is_active ? 'CheckCircleIcon' : 'XCircleIcon'"
                    size="12"
                    class="mr-1"
                  />
                  {{ staff.is_active ? 'Actif' : 'Inactif' }}
                </b-badge>
              </div>

              <div class="supplier-info mb-3">
                <p class="mb-1">
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ staff.email || 'N/A' }}
                </p>
                <p class="mb-1">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ staff.phone || 'N/A' }}
                </p>
                <p class="mb-1">
                  <feather-icon
                    icon="UserIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ getRoleLabel(staff.role) }}
                </p>
                <p class="mb-1">
                  <feather-icon
                    icon="HomeIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ typeof staff.repository === 'string' ? staff.repository :
                    (typeof staff.repository === 'object' && staff.repository ? staff.repository.name : 'N/A') }}
                </p>
              </div>

              <div class="d-flex justify-content-between align-items-center">
                <div />
                <div>
                  <b-button-group size="sm">
                    <b-button
                      v-b-tooltip.hover
                      title="Voir détails"
                      variant="outline-primary"
                      :to="{ name: 'staff-show', params: { id: staff.id }}"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="14"
                      />
                    </b-button>

                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      title="Modifier"
                      variant="outline-secondary"
                      :to="{ name: 'staff-edit', params: { id: staff.id }}"
                    >
                      <feather-icon
                        icon="EditIcon"
                        size="14"
                      />
                    </b-button>

                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      title="Changer le mot de passe"
                      variant="outline-warning"
                      @click="openPasswordChangeModal(staff)"
                    >
                      <feather-icon
                        icon="LockIcon"
                        size="14"
                      />
                    </b-button>

                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      :title="staff.is_active ? 'Désactiver' : 'Activer'"
                      :variant="staff.is_active ? 'outline-danger' : 'outline-success'"
                      @click="toggleStaffStatus(staff)"
                    >
                      <feather-icon
                        :icon="staff.is_active ? 'PowerIcon' : 'CheckCircleIcon'"
                        size="14"
                      />
                    </b-button>
                  </b-button-group>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Aucun membre trouvé -->
    <b-card
      v-if="!isLoading && staffList.length === 0"
      class="text-center py-5"
      aria-label="Aucun membre du personnel trouvé"
      role="status"
      aria-live="polite"
    >
      <div class="empty-state-icon mb-3">
        <feather-icon
          icon="UsersIcon"
          size="60"
          class="text-muted opacity-25"
          aria-hidden="true"
        />
      </div>
      <h4 id="empty-title">
        Aucun membre du personnel trouvé
      </h4>
      <p
        id="empty-desc"
        class="text-muted"
      >
        Ajoutez des membres du personnel ou modifiez vos critères de recherche pour voir les résultats.
      </p>
      <b-button
        :to="{ name: 'staff-add' }"
        variant="primary"
        aria-label="Ajouter un membre du personnel"
      >
        <feather-icon
          icon="PlusIcon"
          aria-hidden="true"
        />
        <span class="ml-1">Ajouter un membre</span>
      </b-button>
    </b-card>

    <!-- Password Change Modal -->
    <b-modal
      id="password-change-modal-staff"
      v-model="showPasswordChangeModal"
      centered
      hide-footer
      size="lg"
      title-tag="div"
      @hidden="resetPasswordChangeModal"
    >
      <template #modal-header="{ close }">
        <div class="w-100">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="password-icon-wrapper-modal mr-2">
                <feather-icon
                  icon="LockIcon"
                  size="20"
                  class="text-primary"
                />
              </div>
              <div>
                <h5 class="mb-0">
                  Changer le mot de passe
                </h5>
                <small
                  v-if="selectedStaffForPassword"
                  class="text-muted"
                >
                  {{ selectedStaffForPassword.username }} • {{ selectedStaffForPassword.email }}
                </small>
              </div>
            </div>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="close"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </div>
        </div>
      </template>

      <div v-if="selectedStaffForPassword">
        <validation-observer
          ref="passwordFormValidator"
          v-slot="{ handleSubmit }"
        >
          <b-form @submit.prevent="handleSubmit(savePasswordChange)">
            <!-- Password Input -->
            <b-row>
              <b-col cols="12">
                <b-form-group
                  label="Nouveau mot de passe *"
                  label-for="modal-password"
                  label-class="form-label-premium"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="modal-password"
                  >
                    <b-input-group class="premium-input-group">
                      <b-form-input
                        id="modal-password"
                        v-model="modalPasswordForm.password"
                        :type="showModalPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : (passwordStrength > 25 ? true : null)"
                        placeholder="Entrez un mot de passe sécurisé"
                        class="premium-input"
                        @input="onPasswordInput"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-secondary"
                          class="password-toggle-btn"
                          @click="showModalPassword = !showModalPassword"
                        >
                          <feather-icon
                            :icon="showModalPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            size="16"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>

                    <!-- Password Strength Indicator -->
                    <div
                      v-if="modalPasswordForm.password"
                      class="password-strength-wrapper mt-2"
                    >
                      <div class="d-flex justify-content-between mb-1">
                        <small class="strength-label">Force du mot de passe :</small>
                        <small
                          class="strength-text"
                          :class="passwordStrengthClass"
                        >{{ passwordStrengthText }}</small>
                      </div>
                      <b-progress
                        :value="passwordStrength"
                        :variant="passwordStrengthClass"
                        max="100"
                        height="6px"
                        class="premium-progress"
                      />
                    </div>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password Input -->
              <b-col cols="12">
                <b-form-group
                  label="Confirmer le mot de passe *"
                  label-for="modal-confirm-password"
                  label-class="form-label-premium"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:modal-password"
                  >
                    <b-input-group class="premium-input-group">
                      <b-form-input
                        id="modal-confirm-password"
                        v-model="modalPasswordForm.confirm_password"
                        :type="showModalConfirmPassword ? 'text' : 'password'"
                        :state="getConfirmPasswordState(errors)"
                        placeholder="Confirmez le mot de passe"
                        class="premium-input"
                        @input="onPasswordInput"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-secondary"
                          class="password-toggle-btn"
                          @click="showModalConfirmPassword = !showModalConfirmPassword"
                        >
                          <feather-icon
                            :icon="showModalConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            size="16"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>

                    <!-- Real-time validation feedback -->
                    <div
                      v-if="modalPasswordForm.confirm_password"
                      class="mt-1"
                    >
                      <small
                        v-if="passwordsMatch && modalPasswordForm.password"
                        class="text-success d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="CheckCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe correspondent
                      </small>
                      <small
                        v-else-if="modalPasswordForm.password"
                        class="text-danger d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="XCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe ne correspondent pas
                      </small>
                      <small
                        v-else
                        class="text-warning d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="AlertTriangleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Veuillez d'abord saisir un mot de passe
                      </small>
                    </div>
                    <small
                      v-if="errors.length > 0 && !modalPasswordForm.confirm_password"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Generate Password Button -->
            <div class="text-center mb-3 mt-2">
              <PasswordGenerator
                :user="selectedStaffForPassword"
                user-type="staff"
                @generated="onPasswordGenerated"
              />
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-between mt-4">
              <b-button
                variant="outline-secondary"
                class="premium-btn-secondary"
                @click="closePasswordChangeModal"
              >
                <feather-icon
                  icon="XIcon"
                  size="16"
                  class="mr-50"
                />
                Annuler
              </b-button>
              <span
                id="password-save-btn-wrapper"
                class="d-inline-block"
              >
                <b-button
                  type="submit"
                  variant="primary"
                  class="premium-btn-primary save-btn-animated"
                  :class="{ 'btn-saving': isSavingPassword, 'not-allowed': !canSavePassword || isSavingPassword }"
                  :disabled="!canSavePassword || isSavingPassword"
                >
                  <b-spinner
                    v-if="isSavingPassword"
                    small
                    class="mr-50"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    size="16"
                    class="mr-50"
                  />
                  Enregistrer
                </b-button>
                <b-tooltip
                  v-if="!canSavePassword || isSavingPassword"
                  target="password-save-btn-wrapper"
                  triggers="hover focus"
                  placement="top"
                >
                  <template v-if="isSavingPassword">
                    Enregistrement en cours...
                  </template>
                  <template v-else-if="modalPasswordForm.password && modalPasswordForm.confirm_password && !passwordsMatch">
                    Les mots de passe ne correspondent pas.
                  </template>
                  <template v-else-if="modalPasswordForm.password && modalPasswordForm.password.length < 8">
                    Le mot de passe doit comporter au moins 8 caractères.
                  </template>
                  <template v-else-if="!modalPasswordForm.password || !modalPasswordForm.confirm_password">
                    Veuillez remplir les champs mot de passe et confirmation.
                  </template>
                  <template v-else>
                    Veuillez remplir correctement les champs.
                  </template>
                </b-tooltip>
              </span>
            </div>
          </b-form>
        </validation-observer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, confirmed, min } from 'vee-validate/dist/rules'
import {
  BButton,
  BCard,
  BCardBody,
  BBadge,
  BRow,
  BCol,
  BFormInput,
  BFormGroup,
  BSpinner,
  BButtonGroup,
  BInputGroup,
  BInputGroupAppend,
  BModal,
  BForm,
  BProgress,
  BTooltip,
} from 'bootstrap-vue'
import storeAuth from '@/store/store'

// Register validation rules
extend('required', required)
extend('confirmed', confirmed)
extend('min', min)

export default {
  components: {
    vSelect,
    PasswordGenerator,
    BButton,
    BCard,
    BCardBody,
    BBadge,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BSpinner,
    BButtonGroup,
    BInputGroup,
    BInputGroupAppend,
    BModal,
    BForm,
    BProgress,
    BTooltip,
    ValidationProvider,
    ValidationObserver,
  },

  data() {
    return {
      columns: [
        {
          label: 'Nom',
          field: 'username',
          sortable: true,
          filterOptions: {
            placeholder: 'Nom',
          },
          width: '200px',
        },
        {
          label: 'Contact',
          field: 'contact_info',
          sortable: false,
          width: '220px',
        },
        {
          label: 'Rôle',
          field: 'role',
          sortable: true,
          width: '120px',
        },
        {
          label: 'Dépôt',
          field: 'repository',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Dépôt',
          },
          width: '150px',
          thAttrs: { title: 'Le dépôt est requis uniquement pour les opérateurs de dépôt' },
        },
        {
          label: 'Statut',
          field: 'is_active',
          sortable: true,
          width: '120px',
        },
        {
          label: 'Actions',
          field: 'action',
          sortable: false,
          width: '140px',
        },
      ],
      staffList: [],
      searchTerm: '',
      isLoading: false,
      pageLength: 100,
      viewMode: 'card', // Changed from 'table' to 'card' - default view is now cards
      repositories: [],
      filters: {
        repository: null,
        is_active: null,
      },
      summaryStats: {
        total: 0,
        active: 0,
        inactive: 0,
        sales: 0,
        warehouse_operator: 0,
        customer_support: 0,
        accountant: 0,
      },
      staffRoles: [
        { label: 'Commerciaux', value: 'sales' },
        { label: 'Opérateurs Dépôt', value: 'warehouse_operator' },
        { label: 'Support Client', value: 'customer_support' },
        { label: 'Comptables', value: 'accountant' },
      ],
      searchTimer: null,
      user: {},
      // Password change modal state
      showPasswordChangeModal: false,
      selectedStaffForPassword: null,
      modalPasswordForm: {
        password: '',
        confirm_password: '',
      },
      showModalPassword: false,
      showModalConfirmPassword: false,
      isSavingPassword: false,
    }
  },

  computed: {
    passwordStrength() {
      let strength = 0
      const { password } = this.modalPasswordForm

      if (!password) return 0

      // Length contribution
      if (password.length >= 8) strength += 25

      // Character type contribution
      if (/[A-Z]/.test(password)) strength += 25 // Uppercase
      if (/[0-9]/.test(password)) strength += 25 // Numbers
      if (/[^A-Za-z0-9]/.test(password)) strength += 25 // Special chars

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.modalPasswordForm.password
        && this.modalPasswordForm.confirm_password
        && this.modalPasswordForm.password === this.modalPasswordForm.confirm_password
      )
    },

    canSavePassword() {
      return (
        this.modalPasswordForm.password
        && this.modalPasswordForm.confirm_password
        && this.passwordsMatch
        && this.modalPasswordForm.password.length >= 8
      )
    },
  },

  created() {
    this.getStaff()
    this.user = storeAuth.state.user
    this.fetchRepositories()
  },

  methods: {
    async getStaff() {
      this.isLoading = true
      try {
        // Build params object for API call
        const params = {}
        if (this.filters.repository) params.repository = this.filters.repository
        if (this.filters.is_active !== null && this.filters.is_active !== undefined) params.is_active = this.filters.is_active
        if (this.searchTerm) params.search = this.searchTerm
        // Only staff roles
        params.role__in = 'sales,warehouse_operator,customer_support,accountant'
        const { data } = await axios.get('/api/auth/staff-members/', { params })
        this.staffList = data.results || []
        this.calculateSummaryStats()
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des membres du personnel')
      } finally {
        this.isLoading = false
      }
    },

    async fetchRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des dépôts')
      }
    },

    calculateSummaryStats() {
      if (!Array.isArray(this.staffList)) {
        this.summaryStats = {
          total: 0,
          active: 0,
          inactive: 0,
          sales: 0,
          warehouse_operator: 0,
          customer_support: 0,
          accountant: 0,
        }
        return
      }
      this.summaryStats = {
        total: this.staffList.length,
        active: this.staffList.filter(s => s.is_active).length,
        inactive: this.staffList.filter(s => !s.is_active).length,
        sales: this.staffList.filter(s => s.role === 'sales').length,
        warehouse_operator: this.staffList.filter(s => s.role === 'warehouse_operator').length,
        customer_support: this.staffList.filter(s => s.role === 'customer_support').length,
        accountant: this.staffList.filter(s => s.role === 'accountant').length,
      }
    },

    async toggleStaffStatus(staff) {
      const action = staff.is_active ? 'désactiver' : 'activer'
      const actionText = staff.is_active ? 'désactivé' : 'activé'
      try {
        const result = await this.$swal({
          title: `${action.charAt(0).toUpperCase() + action.slice(1)} le membre ?`,
          text: `Êtes-vous sûr de vouloir ${action} ${staff.username} ?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: `Oui, ${action}`,
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: `btn ${staff.is_active ? 'btn-warning' : 'btn-success'}`,
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        if (result.value) {
          this.isLoading = true
          try {
            await axios.put(`/api/auth/users/delete/${staff.id}/`)
            await this.getStaff()
            this.showToast('success', 'top-center', `Membre ${actionText} avec succès`)
          } catch (error) {
            this.showToast('danger', 'top-center', `Erreur lors de la modification du statut: ${error.response?.data?.message || error.message}`)
          } finally {
            this.isLoading = false
          }
        }
      } catch (e) {
        // Modal dismissed, do nothing
      }
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: variant === 'success' ? 'Succès' : 'Erreur',
            icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
            text,
            variant,
          },
        },
        { position },
      )
    },

    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'card' : 'table'
    },

    resetFilters() {
      this.searchTerm = ''
      this.filters = {
        repository: null,
        is_active: null,
      }
      this.getStaff()
    },

    onSearchChange() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.getStaff()
      }, 500)
    },

    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    getRoleLabel(role) {
      const map = {
        sales: 'Commercial',
        warehouse_operator: 'Opérateur Dépôt',
        customer_support: 'Support Client',
        accountant: 'Comptable',
      }
      return map[role] || role
    },

    exportToCSV() {
      try {
        // Format data for CSV
        const csvData = this.staffList.map(staff => {
          let depot = ''
          if (staff.repository) {
            if (typeof staff.repository === 'object' && staff.repository) {
              depot = staff.repository.name
            } else {
              depot = staff.repository
            }
          }
          return {
            Nom: staff.username || '',
            Email: staff.email || '',
            'téléphone professionnel': staff.phone || '',
            Rôle: this.getRoleLabel(staff.role),
            Dépôt: depot,
            Statut: staff.is_active ? 'Actif' : 'Inactif',
          }
        })

        // Convert to CSV string
        const headers = Object.keys(csvData[0] || {
          Nom: '', Email: '', Téléphone: '', Rôle: '', Dépôt: '', Statut: '',
        }).join(',')
        const rows = csvData.map(obj => Object.values(obj).map(val => `"${val}"`).join(','))
        const csvContent = [headers, ...rows].join('\n')

        // Create blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        link.setAttribute('href', url)
        link.setAttribute('download', `staff_${new Date().toISOString().slice(0, 10)}.csv`)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.showToast('success', 'top-right', 'Données exportées avec succès')
      } catch (error) {
        this.showToast('danger', 'top-right', "Erreur lors de l'exportation")
      }
    },

    // Password Change Modal Methods
    openPasswordChangeModal(staff) {
      this.selectedStaffForPassword = staff
      this.modalPasswordForm = {
        password: '',
        confirm_password: '',
      }
      this.showModalPassword = false
      this.showModalConfirmPassword = false
      this.showPasswordChangeModal = true
    },

    closePasswordChangeModal() {
      this.showPasswordChangeModal = false
    },

    resetPasswordChangeModal() {
      this.selectedStaffForPassword = null
      this.modalPasswordForm = {
        password: '',
        confirm_password: '',
      }
      this.showModalPassword = false
      this.showModalConfirmPassword = false
      this.isSavingPassword = false

      // Reset validation
      if (this.$refs.passwordFormValidator) {
        this.$refs.passwordFormValidator.reset()
      }
    },

    onPasswordGenerated(generatedPassword) {
      this.modalPasswordForm.password = generatedPassword
      this.modalPasswordForm.confirm_password = generatedPassword
      this.showModalPassword = true
      this.showModalConfirmPassword = true
      this.$forceUpdate()
    },

    onPasswordInput() {
      // Trigger real-time validation
      this.$forceUpdate()
    },

    getConfirmPasswordState(errors) {
      if (errors.length > 0) return false

      // Show validation state only if user has typed in confirm field
      if (!this.modalPasswordForm.confirm_password) return null

      // Real-time match validation
      return !!this.passwordsMatch
    },

    async savePasswordChange() {
      if (!this.canSavePassword || !this.selectedStaffForPassword) return

      this.isSavingPassword = true
      try {
        await axios.post(
          `/api/auth/users/staff/change-password/${this.selectedStaffForPassword.id}/`,
          this.modalPasswordForm,
        )

        this.showToast('success', 'top-right', 'Mot de passe modifié avec succès')
        this.closePasswordChangeModal()
      } catch (error) {
        const errorMsg = error.response?.data?.error
          || error.response?.data?.detail
          || 'Erreur lors du changement de mot de passe'
        this.showToast('danger', 'top-right', errorMsg)
      } finally {
        this.isSavingPassword = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/user-modules.scss";

.supplier-card {
  @extend .user-card;

  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  }

  &.border-success {
    border-color: rgba(40, 167, 69, 0.3) !important;

    &:hover {
      border-color: rgba(40, 167, 69, 0.6) !important;
    }
  }

  &.border-secondary {
    border-color: rgba(108, 117, 125, 0.3) !important;

    &:hover {
      border-color: rgba(108, 117, 125, 0.6) !important;
    }
  }
}

/* Enhanced styling for v-select */
.enhanced-select {
  .vs__dropdown-toggle {
    padding: 4px 8px;
    border: 1px solid #d8d6de !important;
    border-radius: 0.357rem;
    background-color: #f8f8f8;
    transition: all 0.3s ease-in-out;
    min-height: 38px;

    &:hover {
      border-color: #9b1c22 !important;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #9b1c22 !important;
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.1);
  }

  .vs__selected {
    margin: 0 2px;
    padding: 0 5px;
    color: #5e5873;
  }

  .vs__selected-options {
    padding: 2px;
  }

  .vs__search::placeholder {
    color: #b9b9c3;
  }

  .vs__actions {
    padding-right: 8px;
  }

  .vs__dropdown-menu {
    padding: 0;
    border: 1px solid #d8d6de;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  .vs__dropdown-option {
    padding: 8px 20px;

    &:hover, &--highlight {
      background: #f8f8f8;
      color: #9b1c22;
    }
  }
}

/* Specific override for border visibility */
.border-select .vs__dropdown-toggle {
  border: 1px solid #d8d6de !important;
}

/* Enhanced Table Styling */
.vgt-table {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);

  .vgt-table-header {
    background: linear-gradient(135deg, #9b1c22 0%, #0a3370 100%);

    th {
      color: white !important;
      font-weight: 600 !important;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 0.5px;
      border: none !important;
      padding: 1rem 0.75rem;

      &:first-child {
        border-top-left-radius: 0.5rem;
      }

      &:last-child {
        border-top-right-radius: 0.5rem;
      }
    }
  }

  tbody {
    tr {
      transition: all 0.3s ease;

      &:hover {
        background-color: #f8f9fa !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      }

      td {
        padding: 1rem 0.75rem;
        vertical-align: middle;
        border-bottom: 1px solid #ede7f6;
      }
    }
  }
}

/* Enhanced Action Buttons */
.action-buttons {
  .btn-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    }
  }
}

/* Avatar enhancements */
.avatar-wrapper {
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  .status-indicator {
    animation: pulse 2s infinite;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
  }
}

/* Contact info styling */
.contact-info {
  font-size: 0.875rem;

  .feather-icon {
    opacity: 0.7;
  }
}

/* Enhanced badges */
.badge {
  &.badge-pill {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
  }
}

/* Summary cards enhancements */
.card {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
  }
}

.avatar {
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.staff-total-icon {
  background-color: rgba(234, 84, 85, 0.12) !important;
}

/* Enhanced search and filter area */
.card .row {
  .form-group {
    label {
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.5rem;
    }
  }
}

/* Responsive enhancements */
@media (max-width: 768px) {
  .vgt-table {
    font-size: 0.8rem;

    .action-buttons {
      .btn-icon {
        width: 28px;
        height: 28px;
      }
    }
  }

  .avatar-wrapper {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
  }
}

/* Loading state improvements */
.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Enhanced empty state */
.empty-state-icon {
  .feather-icon {
    animation: float 3s ease-in-out infinite;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.status-badge {
  padding: 0.25rem 0.75rem !important;
  font-size: 0.75rem !important;
  min-width: 60px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Password Change Modal Premium Styling */
#password-change-modal-staff {
  ::v-deep .modal-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  }

  ::v-deep .modal-header {
    border-bottom: 1px solid #e2e8f0;
    padding: 1.5rem;
  }

  ::v-deep .modal-body {
    padding: 1.5rem;
  }

  .password-icon-wrapper-modal {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);
  }

  .form-label-premium {
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .generate-password-btn-centered {
    padding: 0.75rem 2rem;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.9375rem;
    border: 2px solid #9b1c22;
    color: #9b1c22;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);

    &:hover {
      background: linear-gradient(135deg, #9b1c22 0%, #0a3370 100%);
      color: #ffffff;
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
      border-color: #9b1c22;
    }

    &:active {
      transform: translateY(0);
    }
  }

  .premium-input-group {
    .form-control {
      border: 1px solid #e2e8f0;
      padding: 0.75rem 1rem;
      border-radius: 8px 0 0 8px;
      font-size: 0.9375rem;
      transition: all 0.2s ease;

      &:focus {
        border-color: #9b1c22;
        box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.1);
      }

      &.is-valid {
        border-color: #28a745;

        &:focus {
          box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.15);
        }
      }

      &.is-invalid {
        border-color: #dc3545;

        &:focus {
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
        }
      }
    }

    .input-group-append {
      .password-toggle-btn {
        border: 1px solid #e2e8f0;
        border-left: none;
        border-radius: 0 8px 8px 0;
        background: #f8fafc;
        color: #64748b;
        padding: 0.75rem 1rem;
        transition: all 0.2s ease;

        &:hover {
          background: #9b1c22;
          color: #ffffff;
          border-color: #9b1c22;
        }
      }
    }
  }

  .password-strength-wrapper {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.75rem;

    .strength-label {
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      font-size: 0.7rem;
    }

    .strength-text {
      font-weight: 700;
      font-size: 0.75rem;

      &.text-danger {
        color: #dc3545 !important;
      }

      &.text-warning {
        color: #ffc107 !important;
      }

      &.text-info {
        color: #17a2b8 !important;
      }

      &.text-success {
        color: #28a745 !important;
      }
    }

    .premium-progress {
      border-radius: 10px;
      background: #e2e8f0;
      overflow: hidden;

      ::v-deep .progress-bar {
        border-radius: 10px;
        transition: width 0.3s ease;
      }
    }
  }

  .premium-btn-secondary {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    border: 1px solid #e2e8f0;
    color: #64748b;
    background: #ffffff;
    transition: all 0.2s ease;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      color: #475569;
      transform: translateY(-1px);
    }
  }

  .premium-btn-primary {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    background: linear-gradient(135deg, #9b1c22 0%, #0a3370 100%);
    border: none;
    color: #ffffff;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &.not-allowed,
    &:disabled {
      opacity: 0.6 !important;
      cursor: not-allowed !important;
      pointer-events: auto !important;
    }

    &.btn-saving {
      animation: pulse-save 1.5s ease-in-out infinite;
    }
  }
}

@keyframes pulse-save {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(13, 67, 153, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(13, 67, 153, 0);
  }
}

/* Validation message animation */
.validation-message-animate {
  animation: fadeInSlide 0.3s ease-out;
}

@keyframes fadeInSlide {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
